import { apiClient } from '@features/shared/api/client'
import type {
  ListTablesResponse,
  TableCapabilitiesResponse,
  TableDetail,
  TableSummary,
  TableDependenciesResponse,
  UpdateTableMetadataRequest,
} from './contracts'
import type {
  ColumnDependenciesResponse,
  ColumnDetail,
  ColumnDuplicationDraft,
  CreateDerivedColumnRequest,
  CreateDuplicateColumnPayload,
  FkDistinctValue,
  FkDistinctValuesResponse,
  PreviewDerivedColumnRequest,
  PreviewDerivedColumnResponse,
  SaveDerivedColumnResponse,
  UpdateColumnMetadataRequest,
  UpdateDerivedColumnRequest,
} from '@features/columns/api/contracts'
import type { Column, ColumnType } from '@features/columns/models'
import type { Table, TableSchema } from '../models'

export async function getTables(): Promise<Table[]> {
  const { data } = await apiClient.get<ListTablesResponse>('/tables')
  return data.tables.map(normalizeTableSummary)
}

export async function getTableCapabilities(): Promise<TableCapabilitiesResponse> {
  const { data } = await apiClient.get<TableCapabilitiesResponse>('/tables/capabilities')
  return data
}

type PendingTableSchemaRequest = {
  resolve: (schema: TableSchema) => void
  reject: (error: unknown) => void
}

const pendingTableSchemaRequests = new Map<string, PendingTableSchemaRequest[]>()
let tableSchemaBatchTimer: ReturnType<typeof setTimeout> | null = null

async function flushTableSchemaBatch() {
  tableSchemaBatchTimer = null

  const pending = new Map(pendingTableSchemaRequests)
  pendingTableSchemaRequests.clear()
  const tableIds = [...pending.keys()]

  try {
    const { data } = await apiClient.post<TableDetail[]>('/tables/meta', tableIds)
    const schemasByTableId = new Map(
      data.map((schema) => [schema.id, normalizeTableSchema(schema)])
    )

    for (const tableId of tableIds) {
      const schema = schemasByTableId.get(tableId)

      if (!schema) {
        const error = new Error(`Table metadata response missing table ${tableId}`)
        pending.get(tableId)?.forEach(({ reject }) => reject(error))
        continue
      }

      pending.get(tableId)?.forEach(({ resolve }) => resolve(schema))
    }
  } catch (error) {
    for (const requests of pending.values()) {
      requests.forEach(({ reject }) => reject(error))
    }
  }
}

export function getTableSchema(tableId: string): Promise<TableSchema> {
  return new Promise((resolve, reject) => {
    const pending = pendingTableSchemaRequests.get(tableId)

    if (pending) {
      pending.push({ resolve, reject })
    } else {
      pendingTableSchemaRequests.set(tableId, [{ resolve, reject }])
    }

    if (tableSchemaBatchTimer === null) {
      tableSchemaBatchTimer = setTimeout(() => void flushTableSchemaBatch(), 0)
    }
  })
}

export async function patchTableMetadata(
  tableId: string,
  payload: UpdateTableMetadataRequest
): Promise<TableSchema> {
  const { data } = await apiClient.patch<TableDetail>(`/tables/${tableId}/meta`, payload)
  const normalizedSchema = normalizeTableSchema(data)

  if ('default_label_column_id' in payload && normalizedSchema.default_label_column == null) {
    return {
      ...normalizedSchema,
      default_label_column: payload.default_label_column_id ?? null,
    }
  }

  return normalizedSchema
}

function normalizeTableSummary(raw: TableSummary): Table {
  return {
    ...raw,
    default_label_column: null,
  } as unknown as Table
}

function normalizeTableSchema(raw: TableDetail): TableSchema {
  const columns = raw.columns.map(normalizeColumn)

  return {
    ...raw,
    table_id: raw.id,
    display_name: raw.display_name_plural,
    default_label_column: raw.default_label_column_id,
    columns,
  }
}

export function normalizeColumn(raw: Partial<ColumnDetail>): Column {
  return {
    ...raw,
    source: raw.is_derived ? 'derived' : 'base',
    type: mapColumnMetadataToColumnType(raw),
    nullable: raw.is_nullable,
  } as Column
}

function normalizeTypeName(value: string | null | undefined): string | null {
  return value?.trim().toLowerCase().replace(/-/g, '_').replace(/ /g, '_') || null
}

function mapColumnMetadataToColumnType(raw: Partial<ColumnDetail>): ColumnType {
  const displayFormat = normalizeTypeName(raw.display_format_name)

  if (displayFormat === 'currency') {
    return 'currency'
  }

  return mapAppDataTypeToColumnType(normalizeTypeName(raw.app_data_type_name))
}

function mapAppDataTypeToColumnType(appDataType: string | null): ColumnType {
  switch (appDataType) {
    case 'number':
    case 'integer':
    case 'decimal':
    case 'fraction':
    case 'percent':
      return 'number'

    case 'currency':
      return 'currency'

    case 'boolean':
    case 'true_false':
    case 'yes_no':
    case 'checkbox':
      return 'boolean'

    case 'date':
      return 'date'
    case 'time':
      return 'time'
    case 'datetime':
      return 'datetime'
    case 'duration':
      return 'duration'
    case 'text':
    case 'email':
    case 'phone_number':
    case 'url':
    case 'id':
    default:
      return 'text'
  }
}

export async function patchColumnMetadata(
  columnId: string,
  payload: UpdateColumnMetadataRequest
): Promise<Column> {
  const { data } = await apiClient.patch<ColumnDetail>(`/columns/${columnId}/meta`, payload)
  return normalizeColumn(data)
}

export async function getColumnMetadata(columnId: string): Promise<Column> {
  const { data } = await apiClient.get<ColumnDetail>(`/columns/${columnId}/meta`)
  return normalizeColumn(data)
}

export async function getColumnDuplicationDraft(
  tableId: string,
  columnId: string
): Promise<ColumnDuplicationDraft> {
  const { data } = await apiClient.get<ColumnDuplicationDraft>(
    `/tables/${tableId}/columns/${columnId}/duplication-draft`
  )
  return data
}

export async function createDuplicateColumn(
  tableId: string,
  columnId: string,
  payload: CreateDuplicateColumnPayload
): Promise<Column> {
  const { data } = await apiClient.post<ColumnDetail>(
    `/tables/${tableId}/columns/${columnId}/duplicate`,
    payload
  )
  return normalizeColumn(data)
}

export async function updateDerivedColumnFormula(
  tableId: string,
  columnId: string,
  payload: UpdateDerivedColumnRequest
): Promise<SaveDerivedColumnResponse> {
  const { data } = await apiClient.put<SaveDerivedColumnResponse>(
    `/tables/${tableId}/derived-columns/${columnId}`,
    payload
  )

  return data
}

export async function createDerivedColumn(
  tableId: string,
  payload: CreateDerivedColumnRequest
): Promise<SaveDerivedColumnResponse> {
  const { data } = await apiClient.post<SaveDerivedColumnResponse>(
    `/tables/${tableId}/derived-columns`,
    payload
  )

  return data
}

export async function getTableDependencies(tableId: string): Promise<TableDependenciesResponse> {
  const { data } = await apiClient.get<TableDependenciesResponse>(
    `/schema-changes/tables/${tableId}/dependencies`
  )
  return data
}

export async function deleteCustomTable(tableId: string): Promise<void> {
  await apiClient.delete(`/tables/${tableId}`)
}

export async function getColumnDependencies(columnId: string): Promise<ColumnDependenciesResponse> {
  const { data } = await apiClient.get<ColumnDependenciesResponse>(
    `/schema-changes/columns/${columnId}/dependencies`
  )
  return data
}

export async function deleteDerivedColumn(tableId: string, columnId: string): Promise<void> {
  await apiClient.delete(`/tables/${tableId}/derived-columns/${columnId}`)
}

export async function deleteCustomColumn(columnId: string): Promise<void> {
  await apiClient.delete(`/columns/${columnId}`)
}

export async function previewDerivedColumn(
  tableId: string,
  payload: PreviewDerivedColumnRequest,
  signal?: AbortSignal
): Promise<PreviewDerivedColumnResponse> {
  const { data } = await apiClient.post<PreviewDerivedColumnResponse>(
    `/tables/${tableId}/derived-columns/preview`,
    payload,
    { signal }
  )

  return data
}

export async function getFkDistinctValues(
  tableId: string,
  columnId: string,
  signal?: AbortSignal
): Promise<FkDistinctValue[]> {
  const { data } = await apiClient.get<FkDistinctValuesResponse>(
    `/tables/${tableId}/columns/${columnId}/distinct`,
    { signal }
  )

  return data.values
}
